import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { trainingCampAPI } from '../services/api';
import {
  CheckCircle,
  Clock,
  Calendar,
  AlertCircle,
  Video,
  FileText,
  User,
  Phone,
  Save,
  Send,
  Lock,
  RefreshCw,
  ExternalLink,
  ChevronRight,
  Info,
  Droplet
} from 'lucide-react';
import './TrainingCampOnboarding.css';

const TrainingCampOnboarding = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Screen states
  const [loading, setLoading] = useState(true);
  const [eligibilityData, setEligibilityData] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [activeStep, setActiveStep] = useState(1);

  // Step 1 Form
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    gender: '',
    dob: '',
    blood_group: '',
    batch_id: '',
    batch_date_label: '',
    video_intro_url: '',
    why_join_url: '',
  });

  const [savingDraft, setSavingDraft] = useState(false);
  const [submittingStep1, setSubmittingStep1] = useState(false);
  const [formError, setFormError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Step 2 Slots
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlotId, setSelectedSlotId] = useState(null);
  const [bookingSlot, setBookingSlot] = useState(false);
  const [rescheduleMode, setRescheduleMode] = useState(false);
  const [bookedSlot, setBookedSlot] = useState(null);

  // 1. Initial Load & Auth Check
  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    loadStatus();
  }, [isAuthenticated, authLoading]);

  const loadStatus = async () => {
    try {
      setLoading(true);
      setFormError('');
      const res = await trainingCampAPI.getMyStatus();
      if (res.data?.success) {
        const data = res.data.data;
        setEligibilityData(data);
        const sub = data.submission;
        const prefill = data.prefill || {};
        const reg = data.registration;
        const regBatches = data.registeredBatches || data.batches || [];
        const defaultBatch = regBatches[0] || null;

        setSubmission(sub);

        // Pre-fill prioritizing saved submission, then registration data, then user profile
        setFormData({
          name: sub?.name || prefill.name || reg?.name || user?.name || '',
          phone: sub?.phone || prefill.phone || reg?.phone || (user?.phone ? String(user.phone).replace(/\D/g, '').slice(-10) : ''),
          gender: sub?.gender || prefill.gender || reg?.gender || '',
          dob: (sub?.dob || prefill.dob || reg?.dob) ? String(sub?.dob || prefill.dob || reg?.dob).split('T')[0] : '',
          blood_group: sub?.bloodGroup || sub?.blood_group || prefill.blood_group || '',
          batch_id: sub?.batchId || sub?.batch_id || prefill.batch_id || (defaultBatch ? String(defaultBatch.id) : ''),
          batch_date_label: sub?.batchDateLabel || sub?.batch_date_label || prefill.batch_date_label || defaultBatch?.date_label || '',
          video_intro_url: sub?.videoIntroUrl || sub?.video_intro_url || '',
          why_join_url: sub?.whyJoinUrl || sub?.why_join_url || '',
        });

        if (sub?.is_step1_submitted) {
          setActiveStep(2);
        } else {
          setActiveStep(1);
        }

        if (sub?.slot) {
          setBookedSlot(sub.slot);
        }

        // If Step 1 already submitted, load slots
        if (sub?.is_step1_submitted) {
          fetchSlots(sub.batch_id || defaultBatch?.id);
        }
      }
    } catch (err) {
      console.error('Failed to load training camp status:', err);
      if (err.response?.status === 401) {
        setFormError('Please login to access the onboarding portal.');
      } else {
        setFormError(err.response?.data?.message || 'Failed to load your registration status. Please refresh.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchSlots = async (batchId) => {
    try {
      setSlotsLoading(true);
      const res = await trainingCampAPI.getAvailableInterviewSlots(batchId || formData.batch_id);
      if (res.data?.success) {
        const slots = res.data.data?.slots || [];
        setAvailableSlots(slots);
        if (slots.length > 0) {
          const firstDate = slots[0].date;
          setSelectedDate(firstDate);
        }
      }
    } catch (err) {
      console.error('Failed to fetch interview slots:', err);
    } finally {
      setSlotsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'batch_id') {
        const batchList = eligibilityData?.registeredBatches?.length
          ? eligibilityData.registeredBatches
          : (eligibilityData?.batches || []);
        const selectedBatch = batchList.find(b => String(b.id) === String(value));
        if (selectedBatch) {
          updated.batch_date_label = selectedBatch.date_label;
        }
      }
      return updated;
    });
    setFormError('');
  };

  // Strict mobile number input handler (numbers only, max 10 digits)
  const handlePhoneChange = (e) => {
    const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 10);
    setFormData(prev => ({ ...prev, phone: digitsOnly }));
    setFormError('');
  };

  // Save Step 1 Draft
  const handleSaveDraft = async () => {
    try {
      setSavingDraft(true);
      setFormError('');
      setSuccessMessage('');

      const res = await trainingCampAPI.saveStep1Draft(formData);
      if (res.data?.success) {
        setSubmission(res.data.data);
        setSuccessMessage('Progress saved as draft! You can safely return and complete this anytime.');
        setTimeout(() => setSuccessMessage(''), 5000);
      }
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to save progress. Please try again.');
    } finally {
      setSavingDraft(false);
    }
  };

  // Submit Step 1
  const handleSubmitStep1 = async (e) => {
    e.preventDefault();
    setFormError('');
    setSuccessMessage('');

    // Strict Validations
    if (!formData.name?.trim()) return setFormError('Please enter your full name.');
    if (!formData.phone || formData.phone.length !== 10 || !/^[6-9]\d{9}$/.test(formData.phone)) {
      return setFormError('Please enter a valid 10-digit mobile number.');
    }
    if (!formData.gender) return setFormError('Please select your gender.');
    if (!formData.dob) return setFormError('Please provide your date of birth.');
    if (!formData.blood_group) return setFormError('Please select your blood group.');
    if (!formData.batch_id) return setFormError('Training camp batch date is required.');
    if (!formData.video_intro_url?.trim()) return setFormError('Please provide your Google Drive link for the Video Introduction.');
    if (!formData.why_join_url?.trim()) return setFormError('Please provide your Google Drive link for "Why you want to join Invincible?".');

    // Google Drive URL check
    if (!formData.video_intro_url.includes('http') || !formData.why_join_url.includes('http')) {
      return setFormError('Please ensure both video links are valid URLs starting with http:// or https://');
    }

    try {
      setSubmittingStep1(true);
      const res = await trainingCampAPI.submitStep1(formData);
      if (res.data?.success) {
        setSubmission(res.data.data);
        setActiveStep(2);
        setSuccessMessage('Step 1 submitted successfully! Please schedule your interview slot below.');
        fetchSlots(formData.batch_id);
      }
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to submit Step 1. Please try again.');
    } finally {
      setSubmittingStep1(false);
    }
  };

  // Step 2: Book Interview Slot
  const handleBookSlot = async () => {
    if (!selectedSlotId) {
      setFormError('Please select a time slot first.');
      return;
    }

    try {
      setBookingSlot(true);
      setFormError('');
      setSuccessMessage('');

      let res;
      if (bookedSlot && rescheduleMode) {
        res = await trainingCampAPI.rescheduleInterviewSlot(selectedSlotId);
      } else {
        res = await trainingCampAPI.bookInterviewSlot(selectedSlotId);
      }

      if (res.data?.success) {
        setSubmission(res.data.data);
        setBookedSlot(res.data.data.slot);
        setRescheduleMode(false);
        setSuccessMessage('Interview slot booked successfully! Our team will meet you at the scheduled time.');
        fetchSlots(submission?.batch_id || formData.batch_id);
      }
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to book slot. It might be fully booked. Please choose another.');
    } finally {
      setBookingSlot(false);
    }
  };

  // Render: Unauthenticated
  if (!isAuthenticated && !authLoading) {
    return (
      <div className="tc-onboarding-container">
        <div className="tc-onboarding-wrapper">
          <div className="tc-card tc-restricted-card">
            <div className="tc-restricted-header">
              <div className="tc-3d-emoji-badge" aria-hidden="true">
                <span className="tc-3d-emoji">🔐</span>
              </div>
              <div className="tc-restricted-header-text">
                <h2 className="tc-restricted-title">Volunteer Login Required</h2>
                <p className="tc-restricted-subtitle">
                  You must be logged in to access the Instructor's Skill Development &amp; Survival Camp onboarding form.
                </p>
              </div>
            </div>
            <div className="tc-restricted-actions">
              <button
                type="button"
                className="tc-btn tc-btn-primary"
                onClick={() => navigate('/login', { state: { from: '/training-camp/onboarding' } })}
              >
                Login with Mobile OTP
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render: Loading
  if (loading || authLoading) {
    return (
      <div className="tc-onboarding-container">
        <div className="tc-onboarding-wrapper">
          <div className="tc-card tc-loading-card">
            <div className="tc-spinner"></div>
            <p>Verifying your Training Camp registration status...</p>
          </div>
        </div>
      </div>
    );
  }

  // Render: Not Eligible (Not registered for an upcoming Camp 35 batch)
  if (eligibilityData && !eligibilityData.isEligible) {
    return (
      <div className="tc-onboarding-container">
        <div className="tc-onboarding-wrapper">
          <div className="tc-card tc-restricted-card">
            {/* Header with 3D Apple Emoji Icon & Titles */}
            <div className="tc-restricted-header">
              <div className="tc-3d-emoji-badge" aria-hidden="true">
                <span className="tc-3d-emoji">⚠️</span>
              </div>
              <div className="tc-restricted-header-text">
                <h2 className="tc-restricted-title">Upcoming Training Camp Registration Required</h2>
                <p className="tc-restricted-subtitle">
                  This onboarding link is exclusively for candidates who have applied for an upcoming batch of the{' '}
                  <strong>Instructor's Skill Development &amp; Survival Camp</strong> (Basic Training Camp).
                </p>
              </div>
            </div>

            {/* Notice Callout Box with Aligned 3D Icon */}
            <div className="tc-notice-box tc-notice-warning">
              <div className="tc-notice-icon-wrap" aria-hidden="true">
                <span className="tc-notice-emoji">💡</span>
              </div>
              <div className="tc-notice-content">
                <h4 className="tc-notice-title">Haven't registered for an upcoming camp yet?</h4>
                <p className="tc-notice-desc">
                  Please register for the camp first on our website to access the volunteer introduction and interview booking.
                </p>
                <p className="tc-notice-subdesc">
                  Already registered with another mobile number? Please log in with your registered phone number.
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="tc-restricted-actions">
              <a
                href="/events/instructors-skill-development-survival-camp-saputara"
                className="tc-btn tc-btn-primary"
                style={{ textDecoration: 'none' }}
              >
                Register for Training Camp
              </a>
              <button
                type="button"
                className="tc-btn tc-btn-secondary"
                onClick={() => navigate('/login', { state: { from: '/training-camp/onboarding' } })}
              >
                Login with Registered Phone
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const registeredBatches = eligibilityData?.registeredBatches || [];
  const hasMultipleBatches = registeredBatches.length > 1;

  // Group slots by date for Cal.com view
  const slotsByDate = availableSlots.reduce((acc, slot) => {
    const d = slot.date;
    if (!acc[d]) acc[d] = [];
    acc[d].push(slot);
    return acc;
  }, {});

  const datesList = Object.keys(slotsByDate).sort();
  const currentDaySlots = selectedDate ? (slotsByDate[selectedDate] || []) : [];

  return (
    <div className="tc-onboarding-container">
      <div className="tc-onboarding-wrapper">
        {/* Stepper Progress */}
        <div className="tc-stepper">
          <div
            className={`tc-step-item ${activeStep === 1 ? 'active' : ''} ${
              submission?.is_step1_submitted ? 'completed' : ''
            }`}
            onClick={() => {
              if (submission?.is_step1_submitted) setActiveStep(1);
            }}
            style={{ cursor: submission?.is_step1_submitted ? 'pointer' : 'default' }}
          >
            <div className="tc-step-badge">
              {submission?.is_step1_submitted ? <CheckCircle size={16} /> : '1'}
            </div>
            <span>Basic Details & Video</span>
          </div>

          <div className={`tc-step-line ${submission?.is_step1_submitted ? 'completed' : ''}`}></div>

          <div
            className={`tc-step-item ${activeStep === 2 ? 'active' : ''} ${
              bookedSlot ? 'completed' : ''
            }`}
            onClick={() => {
              if (submission?.is_step1_submitted) setActiveStep(2);
            }}
            style={{ cursor: submission?.is_step1_submitted ? 'pointer' : 'not-allowed' }}
          >
            <div className="tc-step-badge">
              {bookedSlot ? <CheckCircle size={16} /> : '2'}
            </div>
            <span>Interview Schedule</span>
          </div>
        </div>

        {/* Global Notifications */}
        {formError && (
          <div className="tc-alert tc-alert-error">
            <AlertCircle size={20} />
            <span>{formError}</span>
          </div>
        )}
        {successMessage && (
          <div className="tc-alert tc-alert-success">
            <CheckCircle size={20} />
            <span>{successMessage}</span>
          </div>
        )}

        {/* ================= STEP 1: BASIC DETAILS & VIDEO ================= */}
        {activeStep === 1 && (
          <div className="tc-card">
            {/* Header with Camp Badge */}
            <div className="tc-welcome-header">
              <span className="tc-camp-tag">
                Instructor's Skill Development & Survival Camp
              </span>
              <h1 className="tc-title">Volunteer Candidate Onboarding</h1>
              <div className="tc-welcome-message-box">
                <p className="tc-welcome-lead">Jai Hind!</p>
                <p>
                  We are excited to welcome the young team of volunteers at Invincible. This link is for those, who have applied for the Instructor's Skill Development &amp; Survival Camp, also known as Basic Training Camp. If not then you can visit the website and apply for the same.
                </p>
                <p>
                  The second step in the volunteer's selection is the submission of an introduction. Please watch the video to get more information.
                </p>
                <p>
                  On successful submission of this form, you will need to place an appointment slot for an online video interview.
                </p>
              </div>
            </div>

            {/* YouTube Video Embed - 16:9 full frame */}
            <div className="tc-video-container">
              <iframe
                src="https://www.youtube.com/embed/qQdHjXcH37Y?rel=0"
                title="Invincible Training Camp Briefing"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>

            {/* Locked Notice if already submitted */}
            {submission?.is_step1_submitted && (
              <div className="tc-notice-box tc-notice-locked">
                <Lock size={20} />
                <div>
                  <strong>Step 1 has been submitted and locked.</strong>
                  <p>You cannot edit these details anymore. You can proceed to Step 2 to schedule or manage your interview.</p>
                </div>
                <button
                  type="button"
                  className="tc-btn-small"
                  onClick={() => setActiveStep(2)}
                >
                  Go to Step 2 <ChevronRight size={16} />
                </button>
              </div>
            )}

            {/* Step 1 Form */}
            <form onSubmit={handleSubmitStep1} className="tc-form">
              <div className="tc-form-grid">
                {/* Full Name */}
                <div className="tc-form-group">
                  <label htmlFor="name">
                    <User size={16} /> Full Name <span className="tc-required">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    disabled={submission?.is_step1_submitted}
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    className="tc-input"
                    required
                  />
                </div>

                {/* Mobile No */}
                <div className="tc-form-group">
                  <label htmlFor="phone">
                    <Phone size={16} /> Mobile Number <span className="tc-required">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    maxLength={10}
                    disabled={submission?.is_step1_submitted}
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    placeholder="10-digit mobile number"
                    className="tc-input"
                    required
                  />
                </div>

                {/* Gender */}
                <div className="tc-form-group">
                  <label htmlFor="gender">
                    Gender <span className="tc-required">*</span>
                  </label>
                  <select
                    id="gender"
                    name="gender"
                    disabled={submission?.is_step1_submitted}
                    value={formData.gender}
                    onChange={handleInputChange}
                    className="tc-select"
                    required
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {/* DOB */}
                <div className="tc-form-group">
                  <label htmlFor="dob">
                    <Calendar size={16} /> Date of Birth <span className="tc-required">*</span>
                  </label>
                  <input
                    type="date"
                    id="dob"
                    name="dob"
                    disabled={submission?.is_step1_submitted}
                    value={formData.dob}
                    onChange={handleInputChange}
                    className="tc-input"
                    required
                  />
                </div>

                {/* Training Camp Batch Selection (Dynamic: Non-selective if 1 batch registered; Dropdown if 2+) */}
                <div className="tc-form-group tc-form-group-full">
                  <label htmlFor="batch_id">
                    <Calendar size={16} /> Training Camp Batch Date <span className="tc-required">*</span>
                  </label>

                  {hasMultipleBatches ? (
                    <select
                      id="batch_id"
                      name="batch_id"
                      disabled={submission?.is_step1_submitted}
                      value={formData.batch_id}
                      onChange={handleInputChange}
                      className="tc-select"
                      required
                    >
                      <option value="">Select your Registered Camp Date</option>
                      {registeredBatches.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.date_label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="tc-registered-batch-card">
                      <div className="tc-batch-date-display">
                        <Calendar size={18} className="tc-icon-orange" />
                        <span className="tc-batch-date-text">
                          {formData.batch_date_label || registeredBatches[0]?.date_label}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Field: Blood Group (Placed before Video Introduction Link) */}
                <div className="tc-form-group tc-form-group-full">
                  <label htmlFor="blood_group">
                    <Droplet size={16} /> Blood Group <span className="tc-required">*</span>
                  </label>
                  <select
                    id="blood_group"
                    name="blood_group"
                    disabled={submission?.is_step1_submitted}
                    value={formData.blood_group}
                    onChange={handleInputChange}
                    className="tc-select"
                    required
                  >
                    <option value="">Select Blood Group</option>
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                  </select>
                </div>

              </div>

              {/* Common Google Drive Links Section */}
              <div className="tc-drive-section">
                <div className="tc-drive-section-header">
                  <div className="tc-drive-title">
                    <Video size={20} style={{ color: '#de5824' }} />
                    <h3>Google Drive Links</h3>
                  </div>
                  <p className="tc-drive-subtitle">
                    Upload your vertical video recordings (30–60 sec) to Google Drive and paste the shareable links below.
                  </p>
                </div>

                <div className="tc-form-grid">
                  {/* Video Introduction */}
                  <div className="tc-form-group">
                    <label htmlFor="video_intro_url">
                      Video Introduction <span className="tc-required">*</span>
                    </label>
                    <input
                      type="url"
                      id="video_intro_url"
                      name="video_intro_url"
                      disabled={submission?.is_step1_submitted}
                      value={formData.video_intro_url}
                      onChange={handleInputChange}
                      placeholder="https://drive.google.com/file/d/..."
                      className="tc-input"
                      required
                    />
                  </div>

                  {/* Why you want to join Invincible */}
                  <div className="tc-form-group">
                    <label htmlFor="why_join_url">
                      Why you want to join Invincible? <span className="tc-required">*</span>
                    </label>
                    <input
                      type="url"
                      id="why_join_url"
                      name="why_join_url"
                      disabled={submission?.is_step1_submitted}
                      value={formData.why_join_url}
                      onChange={handleInputChange}
                      placeholder="https://drive.google.com/file/d/..."
                      className="tc-input"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Important Instructions Box */}
              <div className="tc-video-guidelines-box">
                <div className="tc-guidelines-header">
                  <Info size={20} className="tc-icon-amber" />
                  <h4>Important Instructions for Video Links:</h4>
                </div>
                <ul className="tc-guidelines-list">
                  <li>
                    <strong>Vertical Video:</strong> The video must be recorded in portrait mode (vertical orientation).
                  </li>
                  <li>
                    <strong>Length:</strong> Keep the video strictly between <strong>30 seconds to 60 seconds</strong>.
                  </li>
                  <li>
                    <strong>Share Permissions:</strong> Don't forget to set Google Drive link access to{' '}
                    <span className="tc-highlight">"Anyone with the link can view"</span> so our interview panel can review it.
                  </li>
                </ul>
              </div>

              {/* Action Buttons */}
              {!submission?.is_step1_submitted && (
                <div className="tc-form-actions">
                  <button
                    type="button"
                    onClick={handleSaveDraft}
                    disabled={savingDraft || submittingStep1}
                    className="tc-btn tc-btn-secondary"
                  >
                    <Save size={18} />
                    {savingDraft ? 'Saving Draft...' : 'Save Progress'}
                  </button>

                  <button
                    type="submit"
                    disabled={submittingStep1 || savingDraft}
                    className="tc-btn tc-btn-primary"
                  >
                    <Send size={18} />
                    {submittingStep1 ? 'Submitting...' : 'Submit Step 1 & Proceed'}
                  </button>
                </div>
              )}
            </form>
          </div>
        )}

        {/* ================= STEP 2: INTERVIEW SCHEDULING ================= */}
        {activeStep === 2 && (
          <div className="tc-card">
            <div className="tc-welcome-header">
              <span className="tc-camp-tag">Step 2 of 2</span>
              <h1 className="tc-title">Schedule Your Volunteer Interview</h1>
              <p className="tc-subtitle">
                Select your preferred date and time slot from the available options. Each slot has limited seats.
              </p>
            </div>

            {/* If Slot is already booked and NOT in reschedule mode */}
            {bookedSlot && !rescheduleMode ? (
              <div className="tc-booked-confirmation">
                <div className="tc-conf-header">
                  <div className="tc-conf-icon">
                    <CheckCircle size={44} />
                  </div>
                  <h3>Interview Slot Confirmed!</h3>
                  <p>Your interview has been successfully scheduled.</p>
                </div>

                <div className="tc-conf-details-card">
                  <div className="tc-conf-row">
                    <Calendar size={20} />
                    <div>
                      <span className="tc-conf-label">Scheduled Date</span>
                      <span className="tc-conf-val">
                        {new Date(bookedSlot.date + 'T00:00:00').toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </span>
                    </div>
                  </div>

                  <div className="tc-conf-row">
                    <Clock size={20} />
                    <div>
                      <span className="tc-conf-label">Time Slot</span>
                      <span className="tc-conf-val">
                        {bookedSlot.start_time} - {bookedSlot.end_time}
                      </span>
                    </div>
                  </div>

                  {bookedSlot.notes && (
                    <div className="tc-conf-row">
                      <Info size={20} />
                      <div>
                        <span className="tc-conf-label">Instructions / Notes</span>
                        <span className="tc-conf-val">{bookedSlot.notes}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="tc-conf-actions">
                  <button
                    type="button"
                    className="tc-btn-outline"
                    onClick={() => {
                      setRescheduleMode(true);
                      fetchSlots(submission?.batch_id || formData.batch_id);
                    }}
                  >
                    <RefreshCw size={18} /> Reschedule Slot
                  </button>
                  <button
                    type="button"
                    className="tc-btn-secondary"
                    onClick={() => setActiveStep(1)}
                  >
                    View Step 1 Details
                  </button>
                </div>
              </div>
            ) : (
              /* Calendar / Slot Picker (Cal.com style) */
              <div className="tc-cal-wrapper">
                {rescheduleMode && (
                  <div className="tc-notice-box tc-notice-warning" style={{ marginBottom: 20 }}>
                    <Info size={20} />
                    <div>
                      <strong>Rescheduling Interview:</strong> You currently have a slot booked on{' '}
                      <strong>{bookedSlot?.date} ({bookedSlot?.start_time} - {bookedSlot?.end_time})</strong>.
                      Select a new slot below to update.
                    </div>
                    <button
                      type="button"
                      className="tc-btn-small"
                      onClick={() => setRescheduleMode(false)}
                    >
                      Cancel Reschedule
                    </button>
                  </div>
                )}

                {slotsLoading ? (
                  <div className="tc-slots-loading">
                    <div className="tc-spinner"></div>
                    <p>Loading available slots...</p>
                  </div>
                ) : datesList.length === 0 ? (
                  <div className="tc-no-slots">
                    <Calendar size={48} className="tc-icon-muted" />
                    <h4>No Interview Slots Available Currently</h4>
                    <p>
                      The team hasn't opened interview slots for this batch yet, or all current slots are booked.
                      Please check back shortly or reach out to the coordinator.
                    </p>
                  </div>
                ) : (
                  <div className="tc-cal-layout">
                    {/* Left/Top Date Selector */}
                    <div className="tc-cal-dates">
                      <label className="tc-cal-section-title">Select Date</label>
                      <div className="tc-date-pills">
                        {datesList.map((d) => {
                          const dateObj = new Date(d + 'T00:00:00');
                          const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
                          const monthName = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                          const isSelected = selectedDate === d;
                          const availableCount = slotsByDate[d].filter(s => s.available_spots > 0).length;

                          return (
                            <button
                              key={d}
                              type="button"
                              className={`tc-date-pill ${isSelected ? 'active' : ''}`}
                              onClick={() => {
                                setSelectedDate(d);
                                setSelectedSlotId(null);
                              }}
                            >
                              <span className="tc-pill-day">{dayName}</span>
                              <span className="tc-pill-date">{monthName}</span>
                              <span className="tc-pill-slots-count">
                                {availableCount} {availableCount === 1 ? 'slot' : 'slots'}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Right/Bottom Slot Selector */}
                    <div className="tc-cal-slots">
                      <label className="tc-cal-section-title">
                        Available Times for{' '}
                        {selectedDate && (
                          <span>
                            {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', {
                              weekday: 'long',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </span>
                        )}
                      </label>

                      <div className="tc-slots-grid">
                        {currentDaySlots.map((slot) => {
                          const isFull = slot.available_spots <= 0;
                          const isSelected = selectedSlotId === slot.id;
                          const isCurrentBooked = bookedSlot?.id === slot.id;

                          return (
                            <button
                              key={slot.id}
                              type="button"
                              disabled={isFull && !isCurrentBooked}
                              className={`tc-slot-card ${isSelected ? 'selected' : ''} ${
                                isFull ? 'full' : ''
                              } ${isCurrentBooked ? 'current-booked' : ''}`}
                              onClick={() => setSelectedSlotId(slot.id)}
                            >
                              <div className="tc-slot-time">
                                <Clock size={16} />
                                <span>{slot.start_time} - {slot.end_time}</span>
                              </div>
                              <div className="tc-slot-meta">
                                {isCurrentBooked ? (
                                  <span className="tc-spot-badge tc-spot-current">Your Current Slot</span>
                                ) : isFull ? (
                                  <span className="tc-spot-badge tc-spot-full">Full</span>
                                ) : (
                                  <span className="tc-spot-badge tc-spot-available">
                                    {slot.available_spots} {slot.available_spots === 1 ? 'spot' : 'spots'} left
                                  </span>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>

                      {/* Confirm Booking CTA */}
                      <div className="tc-cal-confirm-bar">
                        <button
                          type="button"
                          disabled={!selectedSlotId || bookingSlot}
                          className="tc-btn-primary tc-btn-large"
                          onClick={handleBookSlot}
                        >
                          <CheckCircle size={20} />
                          {bookingSlot
                            ? 'Confirming Slot...'
                            : rescheduleMode
                            ? 'Confirm Rescheduled Slot'
                            : 'Confirm Interview Slot'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TrainingCampOnboarding;
